import type { ReactNode } from 'react';

interface FieldProps {
  id: string;
  label: ReactNode;
  size?: 'md' | 'lg';
  hint?: ReactNode;
  error?: string | undefined;
  children: ReactNode;
}

/** Label + control + hint + error, wired with aria-describedby. */
export function Field({ id, label, size = 'md', hint, error, children }: FieldProps) {
  return (
    <div className="field">
      <label htmlFor={id} className={`field__label${size === 'lg' ? ' field__label--lg' : ''}`}>
        {label}
      </label>
      {hint && (
        <div id={`${id}-hint`} className="field__hint">
          {hint}
        </div>
      )}
      {children}
      {error && (
        <div id={`${id}-error`} className="field__error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

interface FieldsetProps {
  legend: ReactNode;
  size?: 'md' | 'lg';
  hint?: ReactNode;
  error?: string | undefined;
  errorId: string;
  children: ReactNode;
}

/** A group of radios/checkboxes with one shared error. */
export function Fieldset({ legend, size = 'md', hint, error, errorId, children }: FieldsetProps) {
  return (
    <fieldset className="field" aria-describedby={error ? errorId : undefined}>
      <legend className={`field__label${size === 'lg' ? ' field__label--lg' : ''}`}>
        {legend}
      </legend>
      {hint && <div className="field__hint">{hint}</div>}
      {children}
      {error && (
        <div id={errorId} className="field__error" role="alert">
          {error}
        </div>
      )}
    </fieldset>
  );
}
